/**
 * Canonical glossary data captured from the public Train GRC Academy glossary:
 * https://academy.traingrc.com/p/glossary
 * Captured 2026-08-10 from the active dataAWSAuditing and dataCyberRisks arrays.
 *
 * The public default All Courses view combines its active AWS and Containers
 * datasets, then deduplicates exact titles with the last record winning. The
 * duplicate Ephemeral term therefore uses the later definition below. Only
 * surrounding whitespace in source terms and definitions has been trimmed.
 */

export const glossaryCourses = [
  "AWS Auditing Foundations",
  "Demystifying Cyber Risks - Containers",
];

export const glossaryLetters = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "H",
  "I",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
];

export const glossaryEntries = [
  {
    term: "Access Keys",
    definition:
      "A set of credentials used to gain access to an AWS account and make calls against AWS APIs.",
    course: "AWS Auditing Foundations",
    id: "access-keys",
    letter: "A",
  },
  {
    term: "Amazon GuardDuty",
    definition:
      "Amazon GuardDuty is an threat detection tool in AWS that is pre-configured to detect a number of potential AWS-specific threats to your workloads.",
    course: "AWS Auditing Foundations",
    id: "amazon-guardduty",
    letter: "A",
  },
  {
    term: "Amazon Inspector",
    definition:
      "Amazon Inspector is an automated vulnerability management service that continually scans AWS workloads for software vulnerabilities and unintended network exposure.",
    course: "AWS Auditing Foundations",
    id: "amazon-inspector",
    letter: "A",
  },
  {
    term: "Amazon S3",
    definition:
      "Amazon Simple Storage Service is a service that allows any individual or company to store data with arbitrary sizes and data structures.",
    course: "AWS Auditing Foundations",
    id: "amazon-s3",
    letter: "A",
  },
  {
    term: "API (Application Programming Interface)",
    definition:
      "A software intermediary that allows two applications to interact, commonly by making web requests.",
    course: "AWS Auditing Foundations",
    id: "api-application-programming-interface",
    letter: "A",
  },
  {
    term: "Attack Surface Reduction",
    definition:
      "Attack surface reduction is a concept whereby defenders remove tools and hardware/software capabilities that attackers commonly abuse. By removing these tools and capabilities, defenders limit attackers options - reducing the attack surface of a system or environment and the likelihood of attacker success.",
    course: "Demystifying Cyber Risks - Containers",
    id: "attack-surface-reduction",
    letter: "A",
  },
  {
    term: "AWS Account",
    definition:
      "The smallest complete unit of AWS services that exist. You can’t use AWS without using an account.",
    course: "AWS Auditing Foundations",
    id: "aws-account",
    letter: "A",
  },
  {
    term: "AWS CLI (Command-Line Interface)",
    definition:
      "A tool to allow users to interact with various AWS services and resource capabilities via API using text commands.",
    course: "AWS Auditing Foundations",
    id: "aws-cli-command-line-interface",
    letter: "A",
  },
  {
    term: "AWS CloudShell",
    definition:
      "A browser-based shell that allows you to use AWS resources with pre-authentication from your console credentials.",
    course: "AWS Auditing Foundations",
    id: "aws-cloudshell",
    letter: "A",
  },
  {
    term: "AWS CloudTrail",
    definition:
      "A service that enables you to assess, audit, and evaluate the configurations of your AWS resources.",
    course: "AWS Auditing Foundations",
    id: "aws-cloudtrail",
    letter: "A",
  },
  {
    term: "AWS EC2",
    definition:
      "Amazon Elastic Compute Cloud (Amazon EC2) is a web service that allows developers to provision virtual networks and servers in the cloud.",
    course: "AWS Auditing Foundations",
    id: "aws-ec2",
    letter: "A",
  },
  {
    term: "AWS IAM",
    definition:
      "AWS Identity and Access Management allows you to specify who can access specific AWS services and resources, and under which conditions.",
    course: "AWS Auditing Foundations",
    id: "aws-iam",
    letter: "A",
  },
  {
    term: "AWS Organizations",
    definition:
      "Allows an organization to manage a large # of accounts using centralized controls and management capabilities.",
    course: "AWS Auditing Foundations",
    id: "aws-organizations",
    letter: "A",
  },
  {
    term: "AWS Resources",
    definition:
      "The “atomic” unit for AWS. An individual resource may represent an IAM “User” or EC2 “Instance” (Virtual Server).",
    course: "AWS Auditing Foundations",
    id: "aws-resources",
    letter: "A",
  },
  {
    term: "AWS Services",
    definition:
      "The aggregation of common resources. AWS IAM and AWS EC2 are examples of services that aggregate IAM principals and EC2 Virtual Servers.",
    course: "AWS Auditing Foundations",
    id: "aws-services",
    letter: "A",
  },
  {
    term: "AWS SSO",
    definition:
      "AWS Single Sign-On (AWS SSO) is a centralized administration tool where you can control and customize access for (primarily) human identities.",
    course: "AWS Auditing Foundations",
    id: "aws-sso",
    letter: "A",
  },
  {
    term: "Base Image",
    definition:
      "a base image is the source container image used in the creation of a final container image. Commonly, base images are pulled from public or trusted sources and are not created by the organization using the image.",
    course: "Demystifying Cyber Risks - Containers",
    id: "base-image",
    letter: "B",
  },
  {
    term: "Binaries",
    definition:
      "a binary, or executable, file is a file configured to be executed as a software program on a particular operating system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "binaries",
    letter: "B",
  },
  {
    term: "Cardholder Data Environment",
    definition:
      "a computer system or networked group of IT systems that processes, stores and/or transmits cardholder data or sensitive payment authentication data. A CDE also includes any component that directly connects to or supports this network.",
    course: "Demystifying Cyber Risks - Containers",
    id: "cardholder-data-environment",
    letter: "C",
  },
  {
    term: "cgroups",
    definition:
      "Control Groups, or cgroups, are a feature of the Linux kernel that allows the hierarchical arrangement of processes. Cgroups are used in conjunction with namespaces to create containers.",
    course: "Demystifying Cyber Risks - Containers",
    id: "cgroups",
    letter: "C",
  },
  {
    term: "Cloud Environment",
    definition:
      "A cloud environment is composed of a “tenant” environment inside a cloud provider’s platform. For example, an AWS account, Azure subscription, or Google project would be considered a cloud environment.",
    course: "Demystifying Cyber Risks - Containers",
    id: "cloud-environment",
    letter: "C",
  },
  {
    term: "Configuration Files",
    definition:
      "Configuration files are a special type of file in relation to containers and applications. Container configuration files describe metadata regarding how a particular container should be built. Once a container is built based on a particular configuration file, that container image will always start in the same manner.",
    course: "Demystifying Cyber Risks - Containers",
    id: "configuration-files",
    letter: "C",
  },
  {
    term: "Container",
    definition:
      "a lightweight, standalone, executable package of software that includes everything needed to run an application: code, runtime, system tools, system libraries and settings.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container",
    letter: "C",
  },
  {
    term: "Container Breakout",
    definition:
      "a technique used by attackers to \"escalate privileges\" by escaping a container to control the underlying host system. Once a container breakout occurs, an attacker likely controls all containers running on the host system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-breakout",
    letter: "C",
  },
  {
    term: "Container image",
    definition:
      "A container image is a file that contains executable code which can be used to generate a running container instance. Container images are what is “immutable” about containers, if you run a particular container image, it will always run in the same manner - it cannot be changed.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-image",
    letter: "C",
  },
  {
    term: "Container Image Management",
    definition:
      "the technology associated with container runtimes that allows container images to be retrieved and stored locally and remotely.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-image-management",
    letter: "C",
  },
  {
    term: "Container Instances",
    definition:
      "individual containers that are running on a host system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-instances",
    letter: "C",
  },
  {
    term: "Container Layer",
    definition:
      "when building a container image, each step creates a new \"container layer\", where the layer consists of the container image as configured during that build process step.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-layer",
    letter: "C",
  },
  {
    term: "Container Manifest",
    definition:
      "a container manifest is a file stored inside a container image that contains metadata about the container image configuration and build process.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-manifest",
    letter: "C",
  },
  {
    term: "Container Registries",
    definition:
      "container registries are container image hosting services. Common container registries include DockerHub, Artifactory, Quay.io, ECR (AWS), ACR (Azure), and Google Container Registry (GCP).",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-registries",
    letter: "C",
  },
  {
    term: "Container Runtime",
    definition:
      "the engine that allows containers to be built, run, and managed in relation to a host system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-runtime",
    letter: "C",
  },
  {
    term: "Container Volumes",
    definition:
      "Container volumes are data containers that can be exposed to a running container image. \"Mounting\" a volume when running a container means making the data available inside the container.",
    course: "Demystifying Cyber Risks - Containers",
    id: "container-volumes",
    letter: "C",
  },
  {
    term: "Dirty Pipe Vulnerability",
    definition:
      "CVE-2022-0847, a privilege escalation vulnerability in the Linux kernel that allows for container breakouts.",
    course: "Demystifying Cyber Risks - Containers",
    id: "dirty-pipe-vulnerability",
    letter: "D",
  },
  {
    term: "Distroless Image",
    definition:
      "a distroless image is a container image that has been stripped of unnecessary software components and applications. Such an image can prove particularly hard to attack because it removes many of the tools and capabilities an attacker might abuse during exploitation.",
    course: "Demystifying Cyber Risks - Containers",
    id: "distroless-image",
    letter: "D",
  },
  {
    term: "Docker Hub",
    definition:
      "Docker Hub is the original public container registry, and hosts over 9 million container images as of 07/2022.",
    course: "Demystifying Cyber Risks - Containers",
    id: "docker-hub",
    letter: "D",
  },
  {
    term: "Dockerfile",
    definition:
      "a text document that contains all the commands used by a container engine to assemble a container image.",
    course: "Demystifying Cyber Risks - Containers",
    id: "dockerfile",
    letter: "D",
  },
  {
    term: "Ephemeral",
    definition:
      "a concept in computer science that promotes creating and destroying virtual environments on-demand to accomplish specific tasks as the need arises.",
    course: "Demystifying Cyber Risks - Containers",
    id: "ephemeral",
    letter: "E",
  },
  {
    term: "Execute",
    definition:
      "to execute, or run, is to trigger the operation of a software application or a container.",
    course: "Demystifying Cyber Risks - Containers",
    id: "execute",
    letter: "E",
  },
  {
    term: "High-Level Container Runtimes",
    definition:
      "these runtimes provide image management capabilities and allow developers to launch, or execute, a container instance.",
    course: "Demystifying Cyber Risks - Containers",
    id: "high-level-container-runtimes",
    letter: "H",
  },
  {
    term: "Host Isolation",
    definition:
      "Host isolation is a concept that encompasses a variety of techniques for separating computer systems with security 'boundaries'. Weak host isolation indicates the mechanisms used to protect boundaries between these systems are more easily attacked than when compared to other similar boundary mechanisms.",
    course: "Demystifying Cyber Risks - Containers",
    id: "host-isolation",
    letter: "H",
  },
  {
    term: "Host System",
    definition:
      "a physical computer system consisting of the computer hardware and associated firmware required to launch operating systems (OS) and associated software applications.",
    course: "Demystifying Cyber Risks - Containers",
    id: "host-system",
    letter: "H",
  },
  {
    term: "Human Identities",
    definition:
      "Anyone internally or externally that has access to your AWS environments and applications.",
    course: "AWS Auditing Foundations",
    id: "human-identities",
    letter: "H",
  },
  {
    term: "IaaS (Infrastructure as a Service)",
    definition:
      "a cloud computing service provider manages servers and storage, networking firewalls/security, and the data center for you. You control the hosted applications, development tools, database management, business analytics, and operating systems. Compared to PaaS (Platform as a Service) and SaaS (Software as a Service), this offers the most access to the end user.",
    course: "AWS Auditing Foundations",
    id: "iaas-infrastructure-as-a-service",
    letter: "I",
  },
  {
    term: "IAM Principal",
    definition:
      "A user, role or group in AWS IAM that has access to AWS resources.",
    course: "AWS Auditing Foundations",
    id: "iam-principal",
    letter: "I",
  },
  {
    term: "IAM Role",
    definition:
      "an IAM identity that can be set with specific permissions and can be used by anyone that is allowed \"assume\", or gain access to, it.",
    course: "AWS Auditing Foundations",
    id: "iam-role",
    letter: "I",
  },
  {
    term: "IAM User",
    definition:
      "Name and credentials used in AWS to represent a specific person or application that interacts in an AWS environment. It can only be used by that person or application.",
    course: "AWS Auditing Foundations",
    id: "iam-user",
    letter: "I",
  },
  {
    term: "IdP",
    definition:
      "IAM Identity Providers (IdP) allow you to manage identities of external users of AWS, using SSO instead of creating new IAM users.",
    course: "AWS Auditing Foundations",
    id: "idp",
    letter: "I",
  },
  {
    term: "Immutable",
    definition:
      "unable to be changed, unchanging.",
    course: "Demystifying Cyber Risks - Containers",
    id: "immutable",
    letter: "I",
  },
  {
    term: "Initial Access",
    definition:
      "TA0001 in the Mitre ATT&CK framework, initial access is the event where an attacker gains an initial \"foothold\", or operating position, in your organization's environment. Commonly, this occurs when an attacker \"compromises\" or takes control of a user identity and/or computer system with privileges in the environment.",
    course: "Demystifying Cyber Risks - Containers",
    id: "initial-access",
    letter: "I",
  },
  {
    term: "Insecure Container Privileges",
    definition:
      "these are individual permissions that containers may have against a host system that potentially allow for privilege escalation and control of the underlying host system. Bishop Fox provides examples of these privileges and the associated attack techniques in the Bad Pods Github Repository",
    course: "Demystifying Cyber Risks - Containers",
    id: "insecure-container-privileges",
    letter: "I",
  },
  {
    term: "Inspectable",
    definition:
      "to view or examine formally",
    course: "Demystifying Cyber Risks - Containers",
    id: "inspectable",
    letter: "I",
  },
  {
    term: "Interactive Shell",
    definition:
      "an interactive shell is a software utility included in major operating systems (OS) that allows for low-level interaction with the OS. Commonly, interactive shells are used by administrators to perform management activities that would be more tedious and lengthy when using a graphical user interface (GUI).",
    course: "Demystifying Cyber Risks - Containers",
    id: "interactive-shell",
    letter: "I",
  },
  {
    term: "IoT (The Internet of Things)",
    definition:
      "physical things that are connected through a network to the internet.Machine Identities. Any internal or external tools that make requests on your behalf to AWS services, if the tool is provided its own unique identity.",
    course: "AWS Auditing Foundations",
    id: "iot-the-internet-of-things",
    letter: "I",
  },
  {
    term: "Kernel",
    definition:
      "The kernel is the nerve center of a computer operating system (OS). It provides fundamental capabilities to all other OS components and to applications running on the OS. It is the main layer between the OS and hardware, and it performs management of processes and memory, file systems, devices and networking.",
    course: "Demystifying Cyber Risks - Containers",
    id: "kernel",
    letter: "K",
  },
  {
    term: "Kubernetes",
    definition:
      "an open-source system for automating deployment, scaling, and management of containerized applications.",
    course: "Demystifying Cyber Risks - Containers",
    id: "kubernetes",
    letter: "K",
  },
  {
    term: "Lightweight",
    definition:
      "when software is designed to have low memory (RAM) and CPU usage. Lightweight platforms such as containers allow for \"bin-packing\", which can optimize the use of host hardware.",
    course: "Demystifying Cyber Risks - Containers",
    id: "lightweight",
    letter: "L",
  },
  {
    term: "Linux",
    definition:
      "a popular family of operating system distributions based on UNIX. Linux was originally developed by Linus Torvalds, who also developed Git.",
    course: "Demystifying Cyber Risks - Containers",
    id: "linux",
    letter: "L",
  },
  {
    term: "Low-level Container Runtimes",
    definition:
      "these runtimes are responsible for kernel-level interaction with the host system and provisioning an execution environment for container instances. You will likely use a \"Low-level Container Runtime\" in conjunction with a \"High-level Container Runtime\".",
    course: "Demystifying Cyber Risks - Containers",
    id: "low-level-container-runtimes",
    letter: "L",
  },
  {
    term: "Make",
    definition:
      "a software utility that uses source files to create target files with particular modifications specified by a \"Makefile\". Commonly used in software development to perform automated build jobs related to software written for Unix operating systems (OS).",
    course: "Demystifying Cyber Risks - Containers",
    id: "make",
    letter: "M",
  },
  {
    term: "Management Complexity",
    definition:
      "the idea that more complex systems require more management effort.",
    course: "Demystifying Cyber Risks - Containers",
    id: "management-complexity",
    letter: "M",
  },
  {
    term: "MFA (Multi-factor authentication)",
    definition:
      "an authentication feature for account login that requires you to use an additional trusted device or token to validate your identity.",
    course: "AWS Auditing Foundations",
    id: "mfa-multi-factor-authentication",
    letter: "M",
  },
  {
    term: "Microservices",
    definition:
      "breaking apart an application into individual features or capabilities that can be    triggered independently from one another. This makes an application modular and scalable in ways that were previously difficult to achieve in \"monolithic\" application architectures.",
    course: "Demystifying Cyber Risks - Containers",
    id: "microservices",
    letter: "M",
  },
  {
    term: "Monolithic Application",
    definition:
      "Applications built prior to the late 2000’s typically followed a pattern where in order to \"scale\" an application - you had to add servers that included each application component. This is due to the fact that components were not independently scalable. Today, many applications are developed in a manner that allows independent scaling of application components that are overburdened. Independent scaling then creates performance and operational efficiencies for the application.",
    course: "Demystifying Cyber Risks - Containers",
    id: "monolithic-application",
    letter: "M",
  },
  {
    term: "Multi-stage Build",
    definition:
      "a technique for using multiple different base images as part of a container build process. When used for security purposes, this technique can allow developers to reduce the attack and patch management surface of containers, reducing risk and engineering toil.",
    course: "Demystifying Cyber Risks - Containers",
    id: "multi-stage-build",
    letter: "M",
  },
  {
    term: "namespaces",
    definition:
      "namespaces are a feature of the Linux kernel that allows the partitioning of kernel resources so that one set of processes sees one set of resources and another set of processes sees a different set of resources. This allows for compartmentalization of resources, such as containers.",
    course: "Demystifying Cyber Risks - Containers",
    id: "namespaces",
    letter: "N",
  },
  {
    term: "OpenShift",
    definition:
      "is a family of containerization software products developed by Red Hat. OpenShift is a specific version of Kubernetes with a number of configurations managed by Red Hat. OpenShift is commonly configured with different components than a standard Kubernetes installation.",
    course: "Demystifying Cyber Risks - Containers",
    id: "openshift",
    letter: "O",
  },
  {
    term: "Operating System",
    definition:
      "An operating system (OS) is software that manages interactions between computer hardware, software resources, and end users.",
    course: "Demystifying Cyber Risks - Containers",
    id: "operating-system",
    letter: "O",
  },
  {
    term: "Orchestration",
    definition:
      "the automated configuration, management, and coordination of computer systems, applications, and services. For containers, these technologies will allow organizations to run a diverse number of container images for various applications on a single host or a cluster of hosts and provide networking so the container may communicate with each other.",
    course: "Demystifying Cyber Risks - Containers",
    id: "orchestration",
    letter: "O",
  },
  {
    term: "OU (Organizational Unit)",
    definition:
      "allows you to group accounts together to administer as a single unit.",
    course: "AWS Auditing Foundations",
    id: "ou-organizational-unit",
    letter: "O",
  },
  {
    term: "PaaS (Platform as a Service)",
    definition:
      "a cloud computing service provider manages servers and storage, networking firewalls/security, data center buildings, development tools, database management, business analytics, and operating systems for you. You control the hosted applications. This offers you more access than SaaS (Software as a Service), but less than IaaS (Infrastructure as a Service).",
    course: "AWS Auditing Foundations",
    id: "paas-platform-as-a-service",
    letter: "P",
  },
  {
    term: "Package Manager",
    definition:
      "a package manager is a tool used to manage software packages installed on an operating system (OS). You can use a package manager to install, update, and delete software packages from operating systems.",
    course: "Demystifying Cyber Risks - Containers",
    id: "package-manager",
    letter: "P",
  },
  {
    term: "Patch Management",
    definition:
      "Patch management is a technique where organizations ensure the prompt installation of software updates with security implications. These software “patches” have a range of security implications, some of which are critical to the security of systems.",
    course: "Demystifying Cyber Risks - Containers",
    id: "patch-management",
    letter: "P",
  },
  {
    term: "PCI-DSS",
    definition:
      "The Payment Card Industry Data Security Standard is an information security standard for organizations that handle branded credit cards from the major card brands (Visa, Mastercard, etc.).",
    course: "Demystifying Cyber Risks - Containers",
    id: "pci-dss",
    letter: "P",
  },
  {
    term: "Portability",
    definition:
      "Portability is a characteristic of certain types of software where the software can be used on various platforms and operating systems without significant reconfiguration. Containers are portable between container runtimes. Container runtimes can be installed on a wide variety of operating systems and hardware platforms. Therefore, containers are portable.",
    course: "Demystifying Cyber Risks - Containers",
    id: "portability",
    letter: "P",
  },
  {
    term: "Privileged Containers",
    definition:
      "Container running with additional privileges on a container host. If the privileged flag is used when launching a container, that container commonly has full access to the host operating system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "privileged-containers",
    letter: "P",
  },
  {
    term: "Process",
    definition:
      "a process is an instance of a program running on a computer. If you run multiple copies of a software program, a running \"process\" will be allocated for each instance.",
    course: "Demystifying Cyber Risks - Containers",
    id: "process",
    letter: "P",
  },
  {
    term: "Python 3",
    definition:
      "the third generation of the Python programming language runtime. Python 3 allows programmers to develop and run software applications on major operating systems (OS).",
    course: "Demystifying Cyber Risks - Containers",
    id: "python-3",
    letter: "P",
  },
  {
    term: "Runtime",
    definition:
      "includes the necessary software components and program source code to interface with an operating system in order to execute a software program.",
    course: "Demystifying Cyber Risks - Containers",
    id: "runtime",
    letter: "R",
  },
  {
    term: "Runtime Monitoring",
    definition:
      "monitoring inside a running container instance or container instances.",
    course: "Demystifying Cyber Risks - Containers",
    id: "runtime-monitoring",
    letter: "R",
  },
  {
    term: "S3 Buckets",
    definition:
      "containers that are used to store your data in Amazon S3. You must create one of these before you upload anything to S3. When you upload data to s3, you are uploading that data to a specific “bucket”.",
    course: "AWS Auditing Foundations",
    id: "s3-buckets",
    letter: "S",
  },
  {
    term: "SaaS (Software as a Service)",
    definition:
      "a cloud computing service provider controls everything for you - including servers and storage, networking firewalls/security, data center buildings, development tools, database management, business analytics, operating systems, and hosted applications. Compared to IaaS (Infrastructure as a Service) and PaaS (Platform as a Service), this offers you the least access.",
    course: "AWS Auditing Foundations",
    id: "saas-software-as-a-service",
    letter: "S",
  },
  {
    term: "Service Control Policies (SCPs)",
    definition:
      "are a type of organization policy that you can use to manage the maximum allowed permissions for IAM principals in AWS.",
    course: "AWS Auditing Foundations",
    id: "service-control-policies-scps",
    letter: "S",
  },
  {
    term: "Service Mesh",
    definition:
      "is a networking tool for containerized infrastructure that creates a networking infrastructure layer within an application. This allows direct communication between application components that can be analyzed and optimized for performance and security. It may also increase networking complexity for an application.",
    course: "Demystifying Cyber Risks - Containers",
    id: "service-mesh",
    letter: "S",
  },
  {
    term: "SMB",
    definition:
      "Server Message Block (SMB) is a computer protocol that allows servers to share access for printers, file servers, and other resource types to clients (users) over a computer network.",
    course: "Demystifying Cyber Risks - Containers",
    id: "smb",
    letter: "S",
  },
  {
    term: "Socket",
    definition:
      "A socket is a connection \"endpoint\" that allows network communication between a system and other systems on the network.",
    course: "Demystifying Cyber Risks - Containers",
    id: "socket",
    letter: "S",
  },
  {
    term: "Source Code",
    definition:
      "a file with specially formatted text that can be compiled or interpreted into an executable computer program.",
    course: "Demystifying Cyber Risks - Containers",
    id: "source-code",
    letter: "S",
  },
  {
    term: "SSH",
    definition:
      "Secure Shell is a computer protocol that allows users, commonly administrators, to connect to and control a system remotely over a computer network.",
    course: "Demystifying Cyber Risks - Containers",
    id: "ssh",
    letter: "S",
  },
  {
    term: "State",
    definition:
      "In computer science, state is the concept that previous (stored) inputs to a computer program define the current state of the program.",
    course: "Demystifying Cyber Risks - Containers",
    id: "state",
    letter: "S",
  },
  {
    term: "System Libraries",
    definition:
      "Software typically provided as part of an operating system distribution which allows the system to properly function. System libraries allow communication between operating systems and hosted application components, among other capabilities.",
    course: "Demystifying Cyber Risks - Containers",
    id: "system-libraries",
    letter: "S",
  },
  {
    term: "System Resources",
    definition:
      "system resources consist of components such as Central Processing Units (CPUs), Random Access Memory (RAM), and other computer hardware components that support the functionality of the system.",
    course: "Demystifying Cyber Risks - Containers",
    id: "system-resources",
    letter: "S",
  },
  {
    term: "Trusted Access",
    definition:
      "gives you the ability to allow an AWS trusted service to perform tasks within your organization. This allows centralized security management of AWS accounts.",
    course: "AWS Auditing Foundations",
    id: "trusted-access",
    letter: "T",
  },
  {
    term: "Ubuntu",
    definition:
      "a type of Linux operating system (OS) distribution. Ubuntu is almost certainly the most popular Linux distribution for personal computers.",
    course: "Demystifying Cyber Risks - Containers",
    id: "ubuntu",
    letter: "U",
  },
  {
    term: "Untrusted Containers",
    definition:
      "These are container images with unknown provenance. These container images may not be malicious or have known security vulnerabilities...but they may, because you did not generate them and they did not come from a known trusted source.",
    course: "Demystifying Cyber Risks - Containers",
    id: "untrusted-containers",
    letter: "U",
  },
  {
    term: "Virtual Machine",
    definition:
      "an image file managed by a hypervisor that exhibits the behavior of a separate computer, capable of performing tasks such as running applications and programs like a separate computer.",
    course: "Demystifying Cyber Risks - Containers",
    id: "virtual-machine",
    letter: "V",
  },
  {
    term: "Virtualize",
    definition:
      "to create a virtual version of (a computer, operating system, data storage device, etc.), which is not itself an independent device but both works and appears to the user as a unique device.",
    course: "Demystifying Cyber Risks - Containers",
    id: "virtualize",
    letter: "V",
  },
  {
    term: "Vulnerability",
    definition:
      "the quality or state of being exposed to the possibility of being attacked or harmed. In computer security, vulnerabilities are commonly considered to be those software security issues that receive a Common Vulnerability Enumeration (CVE) designation in the list maintained by MITRE.",
    course: "Demystifying Cyber Risks - Containers",
    id: "vulnerability",
    letter: "V",
  },
  {
    term: "Vulnerability Management",
    definition:
      "the process of identifying and prioritizing the remediation of software vulnerabilities using software updates, or “patches\"",
    course: "Demystifying Cyber Risks - Containers",
    id: "vulnerability-management",
    letter: "V",
  },
  {
    term: "Weak Host Isolation",
    definition:
      "a concept in computer security indicating that a particular workload or workload infrastructure component exposes a large attack surface between a host system and the guest operating system (OS) or container. This allows attackers to move from a lower privilege (guest OS or container) platform to a higher privilege (host OS kernel) platform arbitrarily.",
    course: "Demystifying Cyber Risks - Containers",
    id: "weak-host-isolation",
    letter: "W",
  },
  {
    term: "Workload",
    definition:
      "the target of what you are auditing, which might consist of a subset of resources in a single AWS account or be a collection of multiple resources spanning multiple AWS accounts. A small business might have only a few workloads while a large enterprise might have thousands.",
    course: "AWS Auditing Foundations",
    id: "workload",
    letter: "W",
  },
];
